import {
  Browser,
  Buildings,
  ChartBar,
  ChatCircleText,
  FileArrowDown,
  Gauge,
  Notebook,
  Notepad,
  ProjectorScreen,
  QrCode,
  Receipt,
  Users,
} from '@phosphor-icons/react';
import { t } from 'i18next';

import { IAuthUser } from 'auth/types';
import {
  companyImportExportApprovedVendorsPath,
  companyImportExportPathPattern,
  companyPartFinderPathPattern,
} from 'companySettings/routes/routePaths';
import { canAccessCompanySettings } from 'companySettings/utils';
import { customerContactListPath, vendorContactListPath } from 'contacts/routes/routePaths';
import { AccessPermissions, BasePaths } from 'core/enums';
import { IMenuItem } from 'core/types';
import { goTo } from 'core/utils';
import {
  dashboardManageCachePath,
  dashboardManageConsumersPath,
  dashboardSubmitOrderLogPath,
  dashboardVendorQuoteLogPath,
} from 'dashboard/routes/routePaths';
import { elementTemplateListPath } from 'elements/routes';
import { jobListPath } from 'jobs/routes/routePaths';
import { oldMessagesPath } from 'messages/routes/routePaths';
import {
  customerOrderListPath,
  oldOrdersPath,
  vendorOrderListPath,
} from 'orders/routes/routePaths';
import {
  companyPartsPath,
  oldCompanyConfiguratorsPath,
  oldConfiguratorsPath,
  oldElementsPath,
  oldManufacturersPath,
  partListPath,
} from 'parts/routes/routePaths';
import { costingProfileListPath, pricingProfileListPath } from 'pricing/routes/routePaths';
import { projectListPath } from 'projects/routes/routePaths';
import { QuoteType } from 'quotes/enums';
import { customerQuoteListPath, vendorQuoteListPath } from 'quotes/routes/routePaths';
import { quotesDashboardPath, tssDashboardPath } from 'reports/routes/routePaths';
import { supportRequestListPath } from 'support/routes/routePaths';

export const leftMenuStructure = (
  resources: AccessPermissions[] = [],
  user?: IAuthUser
): IMenuItem[] => {
  const hasNewOrderAccess = resources.includes(AccessPermissions.ACCESS_NEW_ORDER);
  const isAdmin = Boolean(user?.admin);

  const companySettingsMatchedPaths = [
    companyImportExportPathPattern(),
    companyPartFinderPathPattern(),
  ];

  const orderMenuItemExtras = (quoteType = QuoteType.CUSTOMER) => {
    if (!hasNewOrderAccess) {
      return {
        onClick: () => goTo(oldOrdersPath()),
      };
    }

    return {
      url: quoteType === QuoteType.CUSTOMER ? customerOrderListPath() : vendorOrderListPath(),
    };
  };

  return [
    {
      name: t('labelProjects'),
      icon: <ProjectorScreen />,
      url: projectListPath(),
      matchedPath: BasePaths.PROJECT,
    },
    {
      name: t('labelJobs'),
      icon: <Notepad />,
      url: jobListPath(),
      matchedPath: BasePaths.JOB,
    },
    {
      name: t('labelQuotes'),
      icon: <Notebook />,
      endDecorator: null,
      subMenuItems: [
        {
          name: t('labelCustomerQuotes'),
          url: customerQuoteListPath(),
        },
        {
          name: t('labelVendorQuotes'),
          url: vendorQuoteListPath(),
        },
      ],
      matchedPath: BasePaths.QUOTE,
    },
    {
      name: t('labelOrders'),
      icon: <QrCode />,
      endDecorator: null,
      subMenuItems: [
        {
          name: t('labelCustomerOrders'),
          ...orderMenuItemExtras(QuoteType.CUSTOMER),
        },
        {
          name: t('labelVendorOrders'),
          ...orderMenuItemExtras(QuoteType.VENDOR),
        },
      ],
      matchedPath: BasePaths.ORDER,
    },
    {
      name: t('labelContacts'),
      icon: <Users />,
      endDecorator: null,
      subMenuItems: [
        {
          name: t('labelCustomerContacts'),
          url: customerContactListPath(),
        },
        {
          name: t('labelVendorContacts'),
          url: vendorContactListPath(),
        },
      ],
      matchedPath: BasePaths.CONTACT,
    },
    {
      name: t('labelPriceCost'),
      icon: <Receipt />,
      endDecorator: null,
      subMenuItems: [
        {
          name: t('labelPricingProfiles'),
          url: pricingProfileListPath(),
          matchedPath: BasePaths.PRICING_PROFILE,
        },
        {
          name: t('labelCostingProfiles'),
          url: costingProfileListPath(),
          matchedPath: BasePaths.COSTING_PROFILE,
        },
      ],
      matchedPath: [BasePaths.PRICING_PROFILE, BasePaths.COSTING_PROFILE],
    },
    {
      name: t('labelMessages'),
      icon: <ChatCircleText />,
      onClick: () => goTo(oldMessagesPath()),
    },
    {
      name: t('labelRequests'),
      icon: <FileArrowDown />,
      url: supportRequestListPath(),
      matchedPath: BasePaths.SUPPORT_REQUEST,
      permissions: [AccessPermissions.SUPPORT_REQUEST],
    },
    {
      name: t('labelReports'),
      icon: <ChartBar />,
      endDecorator: null,
      subMenuItems: [
        {
          name: t('labelTssDashboard'),
          url: tssDashboardPath(),
          permissions: [AccessPermissions.SUPPORT_REPORT],
        },
        {
          name: t('labelQuotesDashboard'),
          url: quotesDashboardPath(),
        },
      ],
      matchedPath: BasePaths.REPORT,
    },
    {
      name: t('labelBrowse'),
      icon: <Browser />,
      endDecorator: null,
      subMenuItems: [
        {
          name: t('labelAllParts'),
          url: partListPath(),
        },
        {
          name: t('labelAllManufacturers'),
          onClick: () => goTo(oldManufacturersPath()),
        },
        {
          name: t('labelAllConfigurators'),
          onClick: () => goTo(oldConfiguratorsPath()),
        },
        {
          name: t('labelAllElements'),
          onClick: () => goTo(oldElementsPath()),
        },
      ],
      matchedPath: [BasePaths.PART],
    },
    {
      name: t('labelCompany'),
      icon: <Buildings />,
      endDecorator: null,
      subMenuItems: [
        {
          name: t('labelCompanyParts'),
          url: companyPartsPath(),
          permissions: [AccessPermissions.COMPANY_PARTS],
        },
        {
          name: t('labelCompanyConfigurators'),
          onClick: () => goTo(oldCompanyConfiguratorsPath()),
        },
        {
          name: t('labelCompanyElements'),
          url: elementTemplateListPath(),
        },
        {
          name: t('labelCompanySettings'),
          url: companyImportExportApprovedVendorsPath(),
          matchedPath: companySettingsMatchedPaths,
          hide: !canAccessCompanySettings(user),
        },
      ],
      matchedPath: companySettingsMatchedPaths,
    },
    ...(isAdmin
      ? [
          {
            name: t('labelDashboard'),
            icon: <Gauge />,
            endDecorator: null,
            subMenuItems: [
              {
                name: t('labelSubmitOrderLog'),
                url: dashboardSubmitOrderLogPath(),
              },
              {
                name: t('labelVendorQuoteLog'),
                url: dashboardVendorQuoteLogPath(),
              },
              {
                name: t('labelManageCache'),
                url: dashboardManageCachePath(),
              },
              {
                name: t('labelManageConsumers'),
                url: dashboardManageConsumersPath(),
              },
            ],
            matchedPath: BasePaths.DASHBOARD,
          },
        ]
      : []),
  ];
};
